import { AlertCircle, RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { ActivityFilters } from "../components/ActivityFilters";
import { ActivityList } from "../components/ActivityList";
import { ActivityScopeBanner } from "../components/ActivityScopeBanner";
import { ActivityStatsStrip } from "../components/ActivityStatsStrip";
import { OperationDrawer } from "../components/OperationDrawer";
import { PaymentsActivityHeader } from "../components/PaymentsActivityHeader";
import { usePaymentsActivity } from "../hooks/usePaymentsActivity";
import { useSelectedOperation } from "../hooks/useSelectedOperation";
import type { ActivityDirection, ActivityRow } from "../types/paymentsActivity.types";
import {
  ACTIVITY_DIRECTION_PARAM,
  ACTIVITY_OPERATION_PARAM,
  parseActivityDirection,
  ACTIVITY_SCOPE_PARTS,
  parseActivityScope,
  withoutActivityParams,
  withoutActivityScope,
} from "../utils/activityLinks";

/**
 * `/app/payments-activity` — read-only list of the org's runtime payins/payouts with live status. Access is
 * decided by the backend RBAC (`payments:read`): a 401/403 latches and renders the no-access view — the
 * legacy scopes array is NOT consulted. This surface never creates/dispatches/cancels anything.
 *
 * The open operation lives in the URL (`?direction=payouts&operation=<uuid>`), not in component state. That
 * makes the drawer linkable — which is what the ledger view points at, and what makes an operation shareable
 * in a support thread — and makes the back button close it. Opening pushes a history entry so that works;
 * closing replaces, so a session of clicking rows does not pile up entries to walk back through.
 */
const PaymentsActivityPage = () => {
  const { t, activeLanguage } = useTranslations("paymentsActivity");
  const { t: tGlobal } = useTranslations("global");
  const [searchParams, setSearchParams] = useSearchParams();

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_billeteras") },
      { label: tGlobal("sidebar_menu_payments_activity"), to: "/app/payments-activity" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  const urlDirection = parseActivityDirection(searchParams.get(ACTIVITY_DIRECTION_PARAM));
  const urlOperationId = searchParams.get(ACTIVITY_OPERATION_PARAM);
  // Read once: the scope a link carried seeds the filters, which the user then owns.
  const [urlScope] = useState(() => parseActivityScope(searchParams));

  const {
    direction,
    setDirection,
    filters,
    setStatusFilter,
    setCountryFilter,
    setRangeDays,
    clearScope,
    clearFilters,
    hasActiveFilters,
    page,
    setPage,
    pageSize,
    total,
    rows,
    isLoading,
    isRefreshing,
    lastUpdatedAt,
    hasError,
    hasAccess,
    refresh,
  } = usePaymentsActivity(urlDirection ?? undefined, urlScope);

  const selected = useSelectedOperation(direction, urlOperationId, rows);

  const openOperation = useCallback(
    (row: ActivityRow) => {
      const next = new URLSearchParams(searchParams);
      next.set(ACTIVITY_DIRECTION_PARAM, row.direction);
      next.set(ACTIVITY_OPERATION_PARAM, row.id);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const closeOperation = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(ACTIVITY_OPERATION_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Dropping a linked scope also drops it from the URL, so a reload does not bring it back.
  const dropScope = useCallback(() => {
    clearScope();
    setSearchParams(withoutActivityScope(searchParams), { replace: true });
  }, [clearScope, searchParams, setSearchParams]);

  const clearAllFilters = useCallback(() => {
    clearFilters();
    setSearchParams(withoutActivityScope(searchParams), { replace: true });
  }, [clearFilters, searchParams, setSearchParams]);

  /**
   * Switching endpoint invalidates the open operation (an id belongs to one direction only) and the status a
   * link brought (each direction has its own vocabulary: a payin status on payouts is a 400 after a reload).
   */
  const handleDirectionChange = useCallback(
    (next: ActivityDirection) => {
      setDirection(next);
      const params = withoutActivityParams(searchParams, ["status"]);
      params.set(ACTIVITY_DIRECTION_PARAM, next);
      params.delete(ACTIVITY_OPERATION_PARAM);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setDirection, setSearchParams],
  );

  // Once the user picks a status or a range, the link's own values leave the URL, so a reload never restores
  // a filter the screen no longer shows.
  const handleStatusChange = useCallback(
    (status: Parameters<typeof setStatusFilter>[0]) => {
      setStatusFilter(status);
      setSearchParams(withoutActivityParams(searchParams, ["status"]), { replace: true });
    },
    [searchParams, setSearchParams, setStatusFilter],
  );
  const handleRangeChange = useCallback(
    (days: number | null) => {
      setRangeDays(days);
      setSearchParams(withoutActivityParams(searchParams, ACTIVITY_SCOPE_PARTS.window), { replace: true });
    },
    [searchParams, setRangeDays, setSearchParams],
  );
  const handleCountryChange = useCallback(
    (country: string | null) => {
      setCountryFilter(country);
      setSearchParams(withoutActivityParams(searchParams, ACTIVITY_SCOPE_PARTS.corridor), { replace: true });
    },
    [searchParams, setCountryFilter, setSearchParams],
  );

  if (!hasAccess) {
    return <NoAccessView />;
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <PaymentsActivityHeader
        actions={
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
            {lastUpdatedAt && (
              <span className="font-mono tabular-nums">
                {t("live_updated_at", {
                  time: lastUpdatedAt.toLocaleTimeString(activeLanguage, {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                })}
              </span>
            )}
            <ColocaIconButton
              icon={RefreshCw}
              variant="ghost"
              size="sm"
              aria-label={t("live_refresh_aria")}
              className={cn(isRefreshing && "[&_svg]:animate-spin")}
              onClick={() => {
                void refresh();
              }}
            />
          </div>
        }
      />

      <ActivityStatsStrip rows={rows} total={total} isLoading={isLoading} />

      {/* A link to an operation that cannot be read must say so — silently ignoring it looks like a bug. */}
      {selected.notFound && (
        <ColocaAlert
          variant="warning"
          icon={AlertCircle}
          text={t("deeplink_not_found")}
          actions={
            <ColocaButton variant="default" size="sm" onClick={closeOperation}>
              {t("deeplink_dismiss")}
            </ColocaButton>
          }
        />
      )}

      {filters.scope && <ActivityScopeBanner scope={filters.scope} onClear={dropScope} />}

      <ActivityFilters
        direction={direction}
        onDirectionChange={handleDirectionChange}
        filters={filters}
        onStatusChange={handleStatusChange}
        onCountryChange={handleCountryChange}
        onRangeDaysChange={handleRangeChange}
        onClear={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Fetch failure: explicit error + retry — never fabricated/zeroed rows. */}
      {hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={
            <span className="flex flex-wrap items-center gap-3">
              {t("error_load")}
              <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={() => void refresh()}>
                {t("error_retry")}
              </ColocaButton>
            </span>
          }
        />
      ) : (
        <ActivityList
          rows={rows}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onRowClick={openOperation}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearAllFilters}
        />
      )}

      {selected.row && <OperationDrawer row={selected.row} open onClose={closeOperation} />}
    </div>
  );
};

export default PaymentsActivityPage;
